import { useEffect, useState } from "react";
import api from "../services/api";

function Report() {
  const [data, setData] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });

  // Currency symbol function
  const getCurrencySymbol = (currencyCode) => {
    const symbols = {
      'USD': '$',
      'AFN': '؋'
    };
    return symbols[currencyCode] || currencyCode;
  };

  // Format number with commas
  const formatNumber = (number) => {
    return Math.round(number).toLocaleString();
  };

  // Filter data by date range
  const filterByDate = (data, dateField = 'date') => {
    if (!dateFilter.startDate && !dateFilter.endDate) return data;

    return data.filter(item => {
      const itemDate = new Date(item[dateField] || item.created_at);
      const start = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
      const end = dateFilter.endDate ? new Date(dateFilter.endDate) : null;

      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;
      return true;
    });
  };

  // Convert amount to AFN using exchange rate
  const convertToAFN = (amount, currency, exchangeRate = 66) => {
    const numericAmount = parseFloat(amount || 0);
    if (currency === 'AFN') return numericAmount;
    return numericAmount * exchangeRate;
  };

  // Format amount with currency symbol
  const formatAmount = (amount, currency = 'USD', showBoth = false, exchangeRate = 66) => {
    if (showBoth && currency === 'USD') {
      return `$${formatNumber(amount)} (؋${formatNumber(convertToAFN(amount, currency, exchangeRate))})`;
    }
    return `${getCurrencySymbol(currency)}${formatNumber(amount)}`;
  };

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        const [incomesRes, expensesRes, withdrawalsRes, paymentsRes, projectsRes, employeesRes] = await Promise.all([
          api.get("incomes/"),
          api.get("expenses/"),
          api.get("withdrawals/"),
          api.get("payments/"),
          api.get("projects/"),
          api.get("employees/")
        ]);

        let incomesData = incomesRes.data;
        let expensesData = expensesRes.data;
        let withdrawalsData = withdrawalsRes.data;
        let paymentsData = paymentsRes.data;
        let projectsData = projectsRes.data;
        let employeesData = employeesRes.data;

        // Apply date filtering
        if (dateFilter.startDate || dateFilter.endDate) {
          incomesData = filterByDate(incomesData, 'date');
          expensesData = filterByDate(expensesData, 'date');
          withdrawalsData = filterByDate(withdrawalsData, 'date');
          paymentsData = filterByDate(paymentsData, 'created_at');
          projectsData = filterByDate(projectsData, 'created_at');
        }

        setIncomes(incomesData);
        setExpenses(expensesData);
        setWithdrawals(withdrawalsData);
        setPayments(paymentsData);
        setProjects(projectsData);
        setEmployees(employeesData);

      } catch (err) {
        console.error(err);
        setError("Failed to fetch report data.");
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [dateFilter]);

  // Calculate financial data
  const calculateFinancialData = () => {
    // Regular Income (from Income table)
    const regularIncome = {
      AFN: incomes.filter(income => income.currency === 'AFN').reduce((sum, income) => sum + parseFloat(income.amount || 0), 0),
      USD: incomes.filter(income => income.currency === 'USD').reduce((sum, income) => sum + parseFloat(income.amount || 0), 0)
    };

    // Project Payments (from Payment table)
    const projectPayments = {
      AFN: payments.filter(payment => payment.currency === 'AFN').reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0),
      USD: payments.filter(payment => payment.currency === 'USD').reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0)
    };

    // Regular Expenses (from Expense table)
    const regularExpenses = {
      AFN: expenses.filter(expense => expense.currency === 'AFN').reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0),
      USD: expenses.filter(expense => expense.currency === 'USD').reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0)
    };

    // Withdrawals
    const withdrawalsData = {
      AFN: withdrawals.filter(withdrawal => withdrawal.currency === 'AFN').reduce((sum, withdrawal) => sum + parseFloat(withdrawal.amount || 0), 0),
      USD: withdrawals.filter(withdrawal => withdrawal.currency === 'USD').reduce((sum, withdrawal) => sum + parseFloat(withdrawal.amount || 0), 0)
    };

    // Project Summary
    const projectSummary = {
      AFN: { budget: 0, paid: 0, remaining: 0 },
      USD: { budget: 0, paid: 0, remaining: 0 },
      total: projects.length
    };

    projects.forEach(project => {
      const currency = project.currency || 'USD';
      const budget = parseFloat(project.budget || 0);
      const paid = parseFloat(project.total_paid || 0);
      const remaining = budget - paid;

      projectSummary[currency].budget += budget;
      projectSummary[currency].paid += paid;
      projectSummary[currency].remaining += remaining;
    });

    // Calculate employee spending
    const employeeSpending = employees.map(employee => {
      const employeeExpenses = expenses.filter(exp => exp.employee === employee.id);
      const totalSpent = employeeExpenses.reduce((sum, exp) => {
        const amount = parseFloat(exp.amount || 0);
        const currency = exp.currency || 'AFN';
        const exchangeRate = parseFloat(exp.exchange_rate || 66);
        
        // Convert to AFN for consistent reporting
        return sum + (amount * exchangeRate);
      }, 0);

      return {
        id: employee.id,
        name: employee.name,
        role: employee.role,
        expenseCount: employeeExpenses.length,
        totalSpent: totalSpent
      };
    }).filter(emp => emp.expenseCount > 0) // Only show employees with expenses
      .sort((a, b) => b.totalSpent - a.totalSpent); // Sort by highest spending

    // Calculate totals
    const totalIncome = {
      AFN: regularIncome.AFN + projectPayments.AFN,
      USD: regularIncome.USD + projectPayments.USD
    };

    const totalExpenses = {
      AFN: regularExpenses.AFN + withdrawalsData.AFN,
      USD: regularExpenses.USD + withdrawalsData.USD
    };

    const netProfit = {
      AFN: totalIncome.AFN - totalExpenses.AFN,
      USD: totalIncome.USD - totalExpenses.USD
    };

    // Get latest USD exchange rate (default to 66)
    let exchangeRate = 66;
    const allRecords = [...incomes, ...expenses, ...withdrawals, ...payments, ...projects];
    const usdRecord = allRecords.find(record => record.currency === 'USD' && record.exchange_rate);
    if (usdRecord && usdRecord.exchange_rate) {
      exchangeRate = parseFloat(usdRecord.exchange_rate);
    }

    // Calculate totals in AFN
    const totalsInAFN = {
      regularIncome: regularIncome.AFN + convertToAFN(regularIncome.USD, 'USD', exchangeRate),
      projectPayments: projectPayments.AFN + convertToAFN(projectPayments.USD, 'USD', exchangeRate),
      totalIncome: totalIncome.AFN + convertToAFN(totalIncome.USD, 'USD', exchangeRate),
      regularExpenses: regularExpenses.AFN + convertToAFN(regularExpenses.USD, 'USD', exchangeRate),
      withdrawals: withdrawalsData.AFN + convertToAFN(withdrawalsData.USD, 'USD', exchangeRate),
      totalExpenses: totalExpenses.AFN + convertToAFN(totalExpenses.USD, 'USD', exchangeRate),
      netProfit: netProfit.AFN + convertToAFN(netProfit.USD, 'USD', exchangeRate),
      projectBudget: projectSummary.AFN.budget + convertToAFN(projectSummary.USD.budget, 'USD', exchangeRate),
      projectPaid: projectSummary.AFN.paid + convertToAFN(projectSummary.USD.paid, 'USD', exchangeRate),
      projectRemaining: projectSummary.AFN.remaining + convertToAFN(projectSummary.USD.remaining, 'USD', exchangeRate),
      employeeSpending: employeeSpending.reduce((sum, emp) => sum + emp.totalSpent, 0)
    };

    return {
      regularIncome,
      projectPayments,
      regularExpenses,
      withdrawals: withdrawalsData,
      totalIncome,
      totalExpenses,
      netProfit,
      projectSummary,
      totalsInAFN,
      exchangeRate,
      employeeSpending
    };
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearDateFilter = () => {
    setDateFilter({
      startDate: '',
      endDate: ''
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>
  );
  
  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="text-2xl">⚠️</span>
      </div>
      <p className="text-red-600 text-lg font-medium">{error}</p>
      <button 
        onClick={() => window.location.reload()}
        className="mt-4 bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
      >
        Try Again
      </button>
    </div>
  );

  const financialData = calculateFinancialData();

  const getNetBalanceColor = (balance) => {
    if (balance > 0) return "text-green-600";
    if (balance < 0) return "text-red-600";
    return "text-gray-600";
  };

  const getNetBalanceBg = (balance) => {
    if (balance > 0) return "from-green-500 to-green-600";
    if (balance < 0) return "from-red-500 to-red-600";
    return "from-gray-500 to-gray-600";
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-2 sm:gap-4">
        <div className="flex-1">
          <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            Financial Report
          </h1>
          <p className="text-gray-600 mt-1 text-xs sm:text-base hidden sm:block">Comprehensive overview of your financial performance</p>
          <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded inline-block mt-1">
            Exchange Rate: 1 USD = {financialData.exchangeRate} AFN
          </div>
        </div>
        
        {/* Export Button */}
        <button className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 sm:px-6 py-1.5 sm:py-3 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-sm hover:shadow-md flex items-center space-x-1 sm:space-x-2 font-medium w-full lg:w-auto justify-center text-xs sm:text-base mt-2 sm:mt-0">
          <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <span>Export</span>
        </button>
      </div>

      {/* Date Filter - Minimal on Mobile */}
      <div className="sm:bg-white sm:border sm:border-gray-200 sm:rounded-lg sm:shadow-sm sm:p-4">
        {/* Mobile: Simple two rows without card styling */}
        <div className="sm:hidden space-y-2">
          <div className="flex space-x-2">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={dateFilter.startDate}
                onChange={handleDateFilterChange}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={dateFilter.endDate}
                onChange={handleDateFilterChange}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={clearDateFilter}
              className="flex-1 px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-xs"
            >
              Clear Filter
            </button>
          </div>
          {(dateFilter.startDate || dateFilter.endDate) && (
            <div className="text-xs text-gray-600 text-center">
              {dateFilter.startDate || 'Beginning'} to {dateFilter.endDate || 'Now'}
            </div>
          )}
        </div>

        {/* Desktop: Card styling */}
        <div className="hidden sm:block">
          <h2 className="text-sm sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Date Range</h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-end">
            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={dateFilter.startDate}
                onChange={handleDateFilterChange}
                className="w-full px-2 py-1.5 text-xs sm:text-base border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={dateFilter.endDate}
                onChange={handleDateFilterChange}
                className="w-full px-2 py-1.5 text-xs sm:text-base border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="w-full sm:w-auto mt-1">
              <button
                onClick={clearDateFilter}
                className="w-full sm:w-auto px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-xs"
              >
                Clear Filter
              </button>
            </div>
          </div>
          {(dateFilter.startDate || dateFilter.endDate) && (
            <div className="mt-1 text-xs text-gray-600">
              {dateFilter.startDate || 'Beginning'} to {dateFilter.endDate || 'Now'}
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards - Very Compact */}
      <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-4 lg:gap-6">
        {/* Total Income */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg sm:rounded-2xl p-2 sm:p-4 lg:p-6 shadow-sm">
          <div className="flex items-center justify-between mb-1 sm:mb-4">
            <h3 className="text-xs sm:text-base lg:text-lg font-semibold text-green-800">Income</h3>
            <div className="w-6 h-6 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-green-500 rounded-lg sm:rounded-xl flex items-center justify-center">
              <span className="text-white text-sm sm:text-lg lg:text-xl">💰</span>
            </div>
          </div>
          <p className="text-sm sm:text-2xl lg:text-3xl font-bold text-green-600 mb-1">
            ؋{formatNumber(financialData.totalsInAFN.totalIncome)}
          </p>
          <div className="w-full bg-green-200 rounded-full h-1 sm:h-2">
            <div 
              className="bg-green-500 h-1 sm:h-2 rounded-full transition-all duration-500"
              style={{ 
                width: `${(financialData.totalsInAFN.totalIncome / (financialData.totalsInAFN.totalIncome + Math.abs(financialData.totalsInAFN.totalExpenses))) * 100}%` 
              }}
            ></div>
          </div>
        </div>

        {/* Regular Expenses */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg sm:rounded-2xl p-2 sm:p-4 lg:p-6 shadow-sm">
          <div className="flex items-center justify-between mb-1 sm:mb-4">
            <h3 className="text-xs sm:text-base lg:text-lg font-semibold text-orange-800">Expenses</h3>
            <div className="w-6 h-6 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-orange-500 rounded-lg sm:rounded-xl flex items-center justify-center">
              <span className="text-white text-sm sm:text-lg lg:text-xl">💸</span>
            </div>
          </div>
          <p className="text-sm sm:text-2xl lg:text-3xl font-bold text-orange-600 mb-1">
            ؋{formatNumber(financialData.totalsInAFN.regularExpenses)}
          </p>
          <div className="w-full bg-orange-200 rounded-full h-1 sm:h-2">
            <div 
              className="bg-orange-500 h-1 sm:h-2 rounded-full transition-all duration-500"
              style={{ 
                width: `${(financialData.totalsInAFN.regularExpenses / financialData.totalsInAFN.totalExpenses) * 100}%` 
              }}
            ></div>
          </div>
        </div>

        {/* Withdrawals */}
        <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-lg sm:rounded-2xl p-2 sm:p-4 lg:p-6 shadow-sm">
          <div className="flex items-center justify-between mb-1 sm:mb-4">
            <h3 className="text-xs sm:text-base lg:text-lg font-semibold text-red-800">Withdrawals</h3>
            <div className="w-6 h-6 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-red-500 rounded-lg sm:rounded-xl flex items-center justify-center">
              <span className="text-white text-sm sm:text-lg lg:text-xl">🏧</span>
            </div>
          </div>
          <p className="text-sm sm:text-2xl lg:text-3xl font-bold text-red-600 mb-1">
            ؋{formatNumber(financialData.totalsInAFN.withdrawals)}
          </p>
          <div className="w-full bg-red-200 rounded-full h-1 sm:h-2">
            <div 
              className="bg-red-500 h-1 sm:h-2 rounded-full transition-all duration-500"
              style={{ 
                width: `${(financialData.totalsInAFN.withdrawals / financialData.totalsInAFN.totalExpenses) * 100}%` 
              }}
            ></div>
          </div>
        </div>

        {/* Net Balance */}
        <div className={`bg-gradient-to-br ${getNetBalanceBg(financialData.totalsInAFN.netProfit)} border border-gray-200 rounded-lg sm:rounded-2xl p-2 sm:p-4 lg:p-6 shadow-sm`}>
          <div className="flex items-center justify-between mb-1 sm:mb-4">
            <h3 className="text-xs sm:text-base lg:text-lg font-semibold text-white">Net</h3>
            <div className="w-6 h-6 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white bg-opacity-20 rounded-lg sm:rounded-xl flex items-center justify-center">
              <span className="text-white text-sm sm:text-lg lg:text-xl">⚖️</span>
            </div>
          </div>
          <p className={`text-sm sm:text-2xl lg:text-3xl font-bold text-white mb-1`}>
            ؋{formatNumber(financialData.totalsInAFN.netProfit)}
          </p>
          <div className="w-full bg-white bg-opacity-30 rounded-full h-1 sm:h-2">
            <div 
              className="bg-white h-1 sm:h-2 rounded-full transition-all duration-500"
              style={{ 
                width: `${Math.min(Math.abs(financialData.totalsInAFN.netProfit) / (financialData.totalsInAFN.totalIncome + Math.abs(financialData.totalsInAFN.totalExpenses)) * 100, 100)}%` 
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Employee Spending Summary */}
      <div className="bg-white rounded-lg sm:rounded-2xl shadow-lg border border-orange-200 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-3 sm:p-4 text-white">
          <div className="flex justify-between items-center">
            <h3 className="text-sm sm:text-lg font-bold">Employee Spending</h3>
            <div className="text-xl sm:text-2xl">👤</div>
          </div>
          <p className="text-orange-100 text-xs sm:text-sm">
            Total spent by employees {dateFilter.startDate || dateFilter.endDate ? '(filtered)' : ''}
          </p>
        </div>
        <div className="p-4 sm:p-6">
          {financialData.employeeSpending.length === 0 ? (
            <div className="text-center py-4">
              <div className="text-gray-400 text-lg mb-2">💸</div>
              <p className="text-gray-500 text-sm">No employee expenses found</p>
              <p className="text-gray-400 text-xs mt-1">
                {dateFilter.startDate || dateFilter.endDate ? 'Try changing date filter' : 'Employees will appear here when they make expenses'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {financialData.employeeSpending.map((employee, index) => (
                <div 
                  key={employee.id} 
                  className="flex justify-between items-center p-3 bg-orange-50 rounded-lg border border-orange-200 hover:bg-orange-100 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-orange-800 text-sm">{employee.name}</div>
                      <div className="text-orange-600 text-xs capitalize">{employee.role}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-orange-600">
                      ؋{formatNumber(employee.totalSpent)}
                    </div>
                    <div className="text-orange-500 text-xs">
                      {employee.expenseCount} expense{employee.expenseCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Total Summary */}
              <div className="mt-4 pt-3 border-t border-orange-200">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-orange-700 text-sm">Total Employee Spending:</span>
                  <span className="text-lg font-bold text-orange-600">
                    ؋{formatNumber(financialData.employeeSpending.reduce((sum, emp) => sum + emp.totalSpent, 0))}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs Navigation - Compact */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg sm:rounded-xl p-1 overflow-x-auto">
        {[
          { id: "overview", label: "📊 Overview", short: "📊" },
          { id: "projects", label: "📁 Projects", short: "📁" },
          { id: "expenses", label: "💰 Expenses", short: "💰" }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 min-w-[60px] sm:min-w-[120px] py-1.5 sm:py-3 px-2 sm:px-4 rounded-md sm:rounded-lg font-medium transition-all duration-200 text-center text-xs sm:text-sm ${
              activeTab === tab.id 
                ? "bg-white text-gray-900 shadow-sm" 
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <span className="sm:hidden">{tab.short}</span>
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Projects Table */}
      {activeTab === "projects" && (
        <div className="bg-white rounded-lg sm:rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-2 sm:p-4 lg:p-6 border-b border-gray-200">
            <h2 className="text-base sm:text-xl font-bold text-gray-900">Projects</h2>
            <p className="text-gray-600 mt-0.5 text-xs sm:text-base hidden sm:block">Detailed breakdown of project finances</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  <th className="p-1 sm:p-3 lg:p-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">Project</th>
                  <th className="p-1 sm:p-3 lg:p-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wide">Budget</th>
                  <th className="p-1 sm:p-3 lg:p-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wide">Paid</th>
                  <th className="p-1 sm:p-3 lg:p-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wide">Remain</th>
                  <th className="p-1 sm:p-3 lg:p-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wide">Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {projects.map((project) => {
                  const progress = project.budget > 0 ? (project.total_paid / project.budget) * 100 : 0;
                  const getProgressColor = (progress) => {
                    if (progress >= 100) return "bg-gradient-to-r from-green-500 to-green-600";
                    if (progress >= 75) return "bg-gradient-to-r from-blue-500 to-blue-600";
                    if (progress >= 50) return "bg-gradient-to-r from-yellow-500 to-yellow-600";
                    return "bg-gradient-to-r from-red-500 to-red-600";
                  };

                  return (
                    <tr key={project.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="p-1 sm:p-3 lg:p-4">
                        <div className="text-xs sm:text-sm font-semibold text-gray-900 truncate max-w-[80px] sm:max-w-none">{project.title}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[80px] sm:max-w-none hidden sm:block">{project.customer?.name}</div>
                      </td>
                      <td className="p-1 sm:p-3 lg:p-4 text-right">
                        <div className="text-xs sm:text-sm font-medium text-gray-900">
                          {formatAmount(project.budget || 0, project.currency || 'USD')}
                        </div>
                      </td>
                      <td className="p-1 sm:p-3 lg:p-4 text-right">
                        <div className="text-xs sm:text-sm font-medium text-green-600">
                          {formatAmount(project.total_paid || 0, project.currency || 'USD')}
                        </div>
                      </td>
                      <td className="p-1 sm:p-3 lg:p-4 text-right">
                        <div className={`text-xs sm:text-sm font-medium ${
                          project.remaining > 0 ? 'text-blue-600' : 'text-gray-600'
                        }`}>
                          {formatAmount(project.remaining || 0, project.currency || 'USD')}
                        </div>
                      </td>
                      <td className="p-1 sm:p-3 lg:p-4">
                        <div className="flex items-center justify-end space-x-1 sm:space-x-3">
                          <div className="w-12 sm:w-20 bg-gray-200 rounded-full h-1.5 sm:h-2">
                            <div 
                              className={`h-1.5 sm:h-2 rounded-full ${getProgressColor(progress)} transition-all duration-300`}
                              style={{ width: `${Math.min(progress, 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-xs font-medium text-gray-700 w-6 sm:w-12 text-right">
                            {progress.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary Footer */}
          <div className="bg-gray-50 px-2 sm:px-4 lg:px-6 py-1.5 sm:py-3 lg:py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-xs space-y-1 sm:space-y-0">
              <span className="text-gray-600">
                {projects.length} projects
              </span>

              <div className="flex flex-col sm:flex-row sm:space-x-3 lg:space-x-6 space-y-0.5 sm:space-y-0 text-xs">
                <div className="text-right">
                  <span className="text-gray-600">Budget: </span>
                  <span className="font-semibold text-gray-900">
                    ؋{formatNumber(financialData.totalsInAFN.projectBudget)}
                  </span>
                </div>

                <div className="text-right">
                  <span className="text-gray-600">Paid: </span>
                  <span className="font-semibold text-green-600">
                    ؋{formatNumber(financialData.totalsInAFN.projectPaid)}
                  </span>
                </div>

                <div className="text-right">
                  <span className="text-gray-600">Remain: </span>
                  <span className="font-semibold text-red-600">
                    ؋{formatNumber(financialData.totalsInAFN.projectRemaining)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
          {/* Financial Summary */}
          <div className="bg-white rounded-lg sm:rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6">
            <h3 className="text-sm sm:text-lg font-bold text-gray-900 mb-2 sm:mb-4">Financial Summary (AFN)</h3>
            <div className="space-y-2 sm:space-y-4">
              {[
                { label: "Regular Income", value: `؋${formatNumber(financialData.regularIncome.AFN)} + $${formatNumber(financialData.regularIncome.USD)}`, color: "text-blue-600" },
                { label: "Project Payments", value: `؋${formatNumber(financialData.projectPayments.AFN)} + $${formatNumber(financialData.projectPayments.USD)}`, color: "text-green-600" },
                { label: "Gross Income", value: `؋${formatNumber(financialData.totalsInAFN.totalIncome)}`, color: "text-green-600", bold: true },
                { label: "Regular Expenses", value: `؋${formatNumber(financialData.regularExpenses.AFN)} + $${formatNumber(financialData.regularExpenses.USD)}`, color: "text-orange-600" },
                { label: "Employee Withdrawals", value: `؋${formatNumber(financialData.withdrawals.AFN)} + $${formatNumber(financialData.withdrawals.USD)}`, color: "text-red-600" },
                { label: "Total Expenses", value: `؋${formatNumber(financialData.totalsInAFN.totalExpenses)}`, color: "text-red-600", bold: true },
                { label: "Net Profit", value: `؋${formatNumber(financialData.totalsInAFN.netProfit)}`, color: getNetBalanceColor(financialData.totalsInAFN.netProfit), bold: true },
                { label: "Profit Margin", value: `${financialData.totalsInAFN.totalIncome > 0 ? ((financialData.totalsInAFN.netProfit / financialData.totalsInAFN.totalIncome) * 100).toFixed(1) : '0.0'}%`, color: getNetBalanceColor(financialData.totalsInAFN.netProfit) }
              ].map((item, index) => (
                <div key={index} className={`flex justify-between items-center py-1.5 sm:py-3 ${index < 7 ? 'border-b border-gray-100' : ''}`}>
                  <span className={`text-gray-600 text-xs sm:text-base ${item.bold ? 'font-semibold' : ''}`}>{item.label}</span>
                  <span className={`font-semibold text-xs ${item.color} text-right`}>{item.value}</span>
                </div>
              ))}
            </div>

            {/* Currency Breakdown */}
            <div className="mt-3 sm:mt-6 p-2 sm:p-4 bg-blue-50 rounded-lg sm:rounded-xl border border-blue-200">
              <h4 className="text-xs sm:text-sm font-semibold text-blue-800 mb-1 sm:mb-3">Currency Breakdown</h4>
              <div className="space-y-1 text-xs sm:text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-700">AFN Income:</span>
                  <span className="font-semibold text-blue-800">؋{formatNumber(financialData.regularIncome.AFN + financialData.projectPayments.AFN)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-700">USD Income:</span>
                  <span className="font-semibold text-blue-800">${formatNumber(financialData.regularIncome.USD + financialData.projectPayments.USD)}</span>
                </div>
                <div className="flex justify-between border-t border-blue-200 pt-1">
                  <span className="text-blue-800 font-semibold">Total:</span>
                  <span className="font-bold text-blue-800">
                    ؋{formatNumber(financialData.totalsInAFN.totalIncome)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-lg sm:rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6">
            <h3 className="text-sm sm:text-lg font-bold text-gray-900 mb-2 sm:mb-4">Quick Stats</h3>
            <div className="space-y-2 sm:space-y-4">
              {[
                { label: "Active Projects", value: projects.length, bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
                { label: "Completed Payments", value: projects.filter(p => parseFloat(p.total_paid || 0) >= parseFloat(p.budget || 0)).length, bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
                { label: "Income Sources", value: incomes.length + payments.length, bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
                { label: "Expense Items", value: expenses.length + withdrawals.length, bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" }
              ].map((stat, index) => (
                <div key={index} className={`flex items-center justify-between p-2 sm:p-4 ${stat.bg} rounded-lg sm:rounded-xl border ${stat.border}`}>
                  <span className={`${stat.text} text-xs sm:text-base`}>{stat.label}</span>
                  <span className={`font-bold ${stat.text.replace('700', '900')} text-sm sm:text-base`}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === "expenses" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
          {/* Expense Breakdown */}
          <div className="bg-white rounded-lg sm:rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6">
            <h3 className="text-sm sm:text-lg font-bold text-gray-900 mb-2 sm:mb-4">Expense Breakdown</h3>
            <div className="space-y-3 sm:space-y-6">
              {/* Expense Distribution */}
              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-3">Distribution</h4>
                <div className="space-y-2 sm:space-y-4">
                  <div>
                    <div className="flex justify-between text-xs sm:text-sm mb-1">
                      <span className="text-gray-600">Regular</span>
                      <span className="font-medium text-orange-600">
                        ؋{formatNumber(financialData.totalsInAFN.regularExpenses)} ({(financialData.totalsInAFN.regularExpenses / financialData.totalsInAFN.totalExpenses * 100).toFixed(1)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-3">
                      <div 
                        className="bg-gradient-to-r from-orange-500 to-orange-600 h-1.5 sm:h-3 rounded-full"
                        style={{ width: `${(financialData.totalsInAFN.regularExpenses / financialData.totalsInAFN.totalExpenses * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs sm:text-sm mb-1">
                      <span className="text-gray-600">Withdrawals</span>
                      <span className="font-medium text-red-600">
                        ؋{formatNumber(financialData.totalsInAFN.withdrawals)} ({(financialData.totalsInAFN.withdrawals / financialData.totalsInAFN.totalExpenses * 100).toFixed(1)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-3">
                      <div 
                        className="bg-gradient-to-r from-red-500 to-red-600 h-1.5 sm:h-3 rounded-full"
                        style={{ width: `${(financialData.totalsInAFN.withdrawals / financialData.totalsInAFN.totalExpenses * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Expenses */}
              <div className="text-center p-3 sm:p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-lg sm:rounded-xl border border-red-200">
                <div className="text-xl sm:text-3xl font-bold text-red-600">
                  ؋{formatNumber(financialData.totalsInAFN.totalExpenses)}
                </div>
                <div className="text-sm sm:text-lg text-red-700 mt-1">Total Expenses</div>
              </div>
            </div>
          </div>

          {/* Recent Withdrawals */}
          <div className="bg-white rounded-lg sm:rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6">
            <h3 className="text-sm sm:text-lg font-bold text-gray-900 mb-2 sm:mb-4">Recent Withdrawals</h3>
            <div className="space-y-2 sm:space-y-4">
              {withdrawals.slice(0, 5).map(withdrawal => (
                <div key={withdrawal.id} className="flex items-center justify-between p-2 sm:p-4 border border-gray-200 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex-1">
                    <p className="text-xs sm:text-sm font-medium text-gray-900">
                      {withdrawal.employee?.name || 'Employee'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {withdrawal.reason || 'No reason'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm sm:text-lg font-bold text-red-600">
                      -{formatAmount(withdrawal.amount || 0, withdrawal.currency || 'USD')}
                    </p>
                  </div>
                </div>
              ))}
              {withdrawals.length === 0 && (
                <div className="text-center py-4 sm:py-8 text-gray-500">
                  <div className="text-2xl sm:text-4xl mb-2 sm:mb-4">💸</div>
                  <div className="text-sm sm:text-lg font-medium">No withdrawals</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Report;